import { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { fetchProactiveNudgeSuggestion } from '../services/index.tsx';
import type { ProactiveAIRecommendation } from '../services/types.tsx';

interface UseProactiveNudgeRecommendationResult {
	recommendation: ProactiveAIRecommendation[] | null;
	error: Error | null;
}

export const useProactiveNudgeRecommendation = (
	triggerType: string,
	triggerDetails: Record<string, unknown> | null = {},
	triggerExperienceId?: string,
): UseProactiveNudgeRecommendationResult => {
	const [recommendation, setRecommendation] = useState<ProactiveAIRecommendation[] | null>(null);
	const [error, setError] = useState<Error | null>(null);

	const fetchRecommendation = useCallback(async () => {
		setError(null);

		const triggerId = uuid();

		try {
			const response = await fetchProactiveNudgeSuggestion({
				triggerId,
				triggerType,
				triggerDetails: triggerDetails || {},
				triggerExperienceId,
			});

			const triggers = response.ai_feature_output?.triggers ?? [];
			const matchingTrigger = triggers.find((trigger) => trigger.trigger_id === triggerId);

			if (!matchingTrigger) {
				setRecommendation(null);
				return;
			}

			const recommendations = matchingTrigger.recommendations ?? [];
			if (recommendations.length === 0) {
				setRecommendation(null);
				return;
			}

			setRecommendation(recommendations);
		} catch (err) {
			const normalizedError = err instanceof Error ? err : new Error(String(err));
			setError(normalizedError);
			setRecommendation(null);
		}
	}, [triggerType, triggerDetails, triggerExperienceId]);

	useEffect(() => {
		if (triggerDetails) {
			fetchRecommendation();
		}
	}, [fetchRecommendation, triggerDetails]);

	return {
		recommendation,
		error,
	};
};
